import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth';
import type { Office, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner } from '../../ui/components';
import { AssistedSessionPanel } from './AssistedSessionPanel';
import { CheckInPanel } from './CheckInPanel';
import { QueuePanel } from './QueuePanel';

const TABS = [
  { key: 'queue', label: 'Queue' },
  { key: 'checkin', label: 'Appointments & check-in' },
  { key: 'assisted', label: 'Assisted session' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function Console() {
  const { persona } = useAuth();
  const officesQ = useApiQuery<Page<Office>>('/api/v1/servicedesk/offices?limit=100');
  const [officeCode, setOfficeCode] = useState<string>('');
  const [tab, setTab] = useState<TabKey>('queue');

  const offices = useMemo(() => officesQ.data?.items ?? [], [officesQ.data]);

  // Default to the persona's home office once the list loads, falling back
  // to the first office alphabetically if they aren't posted anywhere.
  useEffect(() => {
    if (officeCode || offices.length === 0) return;
    const preferred =
      persona?.officeCode && offices.some((o) => o.code === persona.officeCode) ? persona.officeCode : offices[0]!.code;
    setOfficeCode(preferred);
  }, [offices, officeCode, persona?.officeCode]);

  const office = offices.find((o) => o.code === officeCode) ?? null;
  const isCounterOfficer = persona?.roles.includes('COUNTER_OFFICER') ?? false;

  return (
    <div>
      <PageHeader
        title="Counter Console"
        subtitle="Call the next ticket, check citizens in, and run assisted counter sessions."
        actions={
          <label className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-slate-500">Office</span>
            <select className="field-input !w-auto" value={officeCode} onChange={(e) => setOfficeCode(e.target.value)}>
              {offices.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name} ({o.code})
                </option>
              ))}
            </select>
          </label>
        }
      />

      {officesQ.error ? <ErrorNote message={officesQ.error} /> : null}
      {officesQ.loading ? <Spinner label="Loading offices…" /> : null}

      {!isCounterOfficer ? (
        <div className="mb-4">
          <ErrorNote message="Your persona does not hold COUNTER_OFFICER — queue-calling and appointment lifecycle actions will be rejected by the API (403). You can still look things up." />
        </div>
      ) : null}

      {office ? (
        <>
          <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'queue' ? <QueuePanel office={office} /> : null}
          {tab === 'checkin' ? <CheckInPanel office={office} /> : null}
          {tab === 'assisted' ? <AssistedSessionPanel office={office} /> : null}
        </>
      ) : null}
    </div>
  );
}
